import React from 'react';
import Yup from '~/util/yup';
import { getFacilityOptions, editFacilityOptions, addFacilityOptions, FacilityTypeOptions } from '@generated';
import { useSuspenseQuery } from '@tanstack/react-query';
import { TextFormField } from '~/shared/forms/controls/fields/text-fields/TextFormField';
import { AddressFormField } from '~/shared/forms/controls/fields/AddressFormField';
import { StethoscopeIcon } from 'lucide-react';
import { LibraryForm } from '../shared/LibraryForm';
import { useNavigate, useParams } from 'react-router-dom';
import { PhoneNumberFormField } from '~/shared/forms/controls/fields/text-fields/PhoneNumberFormField';
import { TextAreaFormField } from '~/shared/forms/controls/fields/text-fields/TextAreaFormField';
import type { FormProps } from '~/shared/forms/Form';
import { SelectFormField } from '~/shared/forms/controls/fields/SelectFormField';
import { getSearchQueryKey } from '~/util/http/useSearch';

const validationSchema = Yup.object().shape({
	name: Yup.string().label('Facility Name').nullable().required(),
	npiNumber: Yup.string().label('NPI Number').nullable().required().npi(),
	address: Yup.object().label('Address').address().required(),
	phoneNumber: Yup.string().label('Phone Number').nullable().required().phoneNumber(),
	faxNumber: Yup.string().label('Fax Number').nullable().phoneNumber(),
	emailAddress: Yup.string().label('Email Address').nullable().email(),
	notes: Yup.string().label('Notes').nullable(),
	facilityTypeId: Yup.number().label('Facility Type').nullable(),
	hoursOfOperation: Yup.string().label('Hours of Operation').nullable(),
	faciltyWebsite: Yup.string().label('Facility Website').nullable().url(),
	contact: Yup.object()
		.shape({
			name: Yup.string().label('Contact Name').nullable(),
			phoneNumber: Yup.string().label('Contact Phone Number').nullable().phoneNumber(),
			emailAddress: Yup.string().label('Contact Email Address').nullable().email(),
			notes: Yup.string().label('Contact Notes').nullable()
		})
		.nullable()
});

export function EditFacilityForm() {
	const { id } = useParams<'id'>();
	const options = getFacilityOptions(id!);
	const { data: facility } = useSuspenseQuery(options);

	return (
		<FacilityForm
			initialValues={{ ...facility, facilityId: id }}
			saveMutation={{
				...editFacilityOptions,
				invalidateQueryKeys: [options.queryKey]
			}}
			title={`Edit Facility: ${facility?.name}`}
			description={`Make changes to the facility ${facility?.name}`}
		/>
	);
}

export function AddFacilityForm() {
	const navigate = useNavigate();
	return (
		<FacilityForm
			initialValues={{
				name: null,
				npiNumber: null,
				facilityTypeId: null,
				address: null,
				phoneNumber: null,
				faxNumber: null,
				notes: null,
				hoursOfOperation: null,
				faciltyWebsite: null,
				contact: null
			}}
			saveMutation={{
				...addFacilityOptions,
				invalidateQueryKeys: [getSearchQueryKey('facility')]
			}}
			onSuccess={() => navigate('/settings/libraries/facilities')}
			title="Add Facility"
			description="Add a facility to the system"
		/>
	);
}

function FacilityForm(props: Pick<FormProps, 'initialValues' | 'saveMutation' | 'onSuccess'> & { title: string; description: string }) {
	const navigate = useNavigate();
	return (
		<LibraryForm
			{...props}
			TitleIcon={StethoscopeIcon}
			validationSchema={validationSchema}
			errorAction={{
				errorActionText: 'Return to Facility Library',
				errorAction: () => navigate('/settings/libraries/pharmacies')
			}}
		>
			<div className="grid grid-cols-2 gap-6">
				<TextFormField name="firstName" />
				<TextFormField name="lastName" />
				<TextFormField name="suffix" />
				<TextFormField name="practiceName" />
				<TextFormField name="npiNumber" />
				<PhoneNumberFormField name="phoneNumber" />
				<PhoneNumberFormField name="faxNumber" />

				<TextFormField name="emailAddress" className="col-span-2" />
				<SelectFormField name="facilityTypeId" options={FacilityTypeOptions} />
				<TextAreaFormField name="notes" className="col-span-2" />
				<div className="col-span-2">
					<AddressFormField name="address" />
				</div>
			</div>
		</LibraryForm>
	);
}
